import { asInstant } from '@gridfit/core/businessdate'
import type { Instant } from '@gridfit/core/businessdate'
import { ulidFromParts } from '@gridfit/core/ids'
import type { Ulid } from '@gridfit/core/ids'

/**
 * The only module in the workspace permitted to read the wall clock or draw
 * randomness. Everything downstream receives these values as arguments.
 */

let lastTimestampMs = -1
let lastRandomness: Uint8Array<ArrayBuffer> = new Uint8Array(10)

export function newUlid(): Ulid {
  const timestampMs = Date.now()

  if (timestampMs === lastTimestampMs) {
    // Same millisecond: increment the previous randomness so ids stay sortable
    // and unique, per the ulid spec's monotonicity rule.
    lastRandomness = increment(lastRandomness)
  } else {
    lastTimestampMs = timestampMs
    lastRandomness = randomBytes(10)
  }

  return ulidFromParts(timestampMs, lastRandomness)
}

export function nowInstant(): Instant {
  return asInstant(new Date().toISOString())
}

export function randomToken(bytes: number): string {
  return Array.from(randomBytes(bytes), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * A string of `length` characters drawn uniformly at random from `alphabet`.
 * Kiosk activation codes (`packages/api/src/auth/kiosk.ts`) are the first
 * caller — a human reads the code down a phone, so the CALLER chooses an
 * alphabet excluding characters that get misread aloud (`O`/`0`, `I`/`1`) —
 * this function only draws the randomness, the one thing this package exists
 * to be the sole legal source of.
 *
 * Requires `256 % alphabet.length === 0`: each character comes from one
 * random byte reduced modulo `alphabet.length`, and only an even divisor of
 * 256 keeps that reduction perfectly uniform (32 divides 256 exactly 8 times,
 * so `byte % 32` lands on every index with identical probability). An
 * alphabet size that does not divide 256 evenly would draw its tail
 * characters very slightly less often than the rest — a bias nobody would
 * ever notice, which is exactly why it is refused here rather than shipped.
 */
export function randomCode(alphabet: string, length: number): string {
  if (alphabet.length === 0 || 256 % alphabet.length !== 0) {
    throw new Error(
      `randomCode: alphabet length ${alphabet.length} does not evenly divide 256; character draws would be biased`,
    )
  }
  return Array.from(randomBytes(length), (byte) => alphabet[byte % alphabet.length]).join('')
}

function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(length)
  crypto.getRandomValues(out)
  return out
}

function increment(bytes: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> {
  const out = Uint8Array.from(bytes)
  for (let i = out.length - 1; i >= 0; i--) {
    const next = (out[i] as number) + 1
    out[i] = next & 0xff
    if (next <= 0xff) return out
  }
  throw new Error('ulid randomness overflowed within a single millisecond')
}
